import { z } from 'zod';
import register from './legal/documents.json' with { type: 'json' };

/**
 * Every legal document, who it is for and where it lives (WEB-007) - read from
 * ONE register, `legal/documents.json`, by every surface that links a document:
 * the guest storefront's footer and checkout, the partner portal's sign-up line,
 * the worker that records an acceptance, and `website/tools/build-pages.py`,
 * which renders www.'s footer and `docs/legal/index.html` from the same file.
 *
 * The rule the register exists to hold: **each audience reads its own terms,
 * where it acts.** A guest reads the terms of sale on the storefront it is
 * ordering on; an owner or a conciergerie reads the partnership conditions on
 * www. and accepts them at sign-up; a bakery signs its contract and finds it
 * published nowhere. A surface lists only documents of its own audience or of
 * everyone, and `legal.test.ts` fails the build on any other.
 */

const Locale = z.enum(['fr', 'en']);
export type LegalLocale = z.infer<typeof Locale>;
const PerLocale = z.object({ fr: z.string().min(1), en: z.string().min(1).optional() });

export const LegalAudience = z.enum(['guest', 'partner', 'bakery', 'everyone']);
export type LegalAudience = z.infer<typeof LegalAudience>;

/** The host a document is served on, named by its subdomain - never a full host (OPS-019). */
export const LegalHost = z.enum(['order', 'www']);
export type LegalHost = z.infer<typeof LegalHost>;

const LegalDocumentSchema = z
  .object({
    id: z.string().min(1),
    audience: LegalAudience,
    title: PerLocale,
    /** A shorter link text for a footer, where the surface already says who the document is for. */
    label: PerLocale.optional(),
    /** A date (`2026-08-19`) or a contract version (`1.1`), printed on the document itself. */
    version: z.string().min(1),
    /** The file that IS the document, from the repository root; edit it, never a copy. */
    master: PerLocale,
    /** Null for a contract that is signed and never published. */
    host: LegalHost.nullable(),
    path: PerLocale.nullable(),
    acceptance: z.enum(['checkout', 'partner-sign-up', 'signature', 'none']),
  })
  .refine(
    (d) => (d.host === null) === (d.path === null),
    'host and path are both set or both null',
  );
export type LegalDocument = z.infer<typeof LegalDocumentSchema>;

export const LegalSurface = z.enum(['order', 'www', 'app']);
export type LegalSurface = z.infer<typeof LegalSurface>;

const RegisterSchema = z.object({
  documents: z.array(LegalDocumentSchema).min(1),
  surfaces: z.record(
    LegalSurface,
    z.object({ audience: LegalAudience, documents: z.array(z.string()).min(1) }),
  ),
});

const parsed = RegisterSchema.parse(register);

export const LEGAL_DOCUMENTS: readonly LegalDocument[] = parsed.documents;
export const LEGAL_SURFACES = parsed.surfaces as Readonly<
  Record<LegalSurface, { readonly audience: LegalAudience; readonly documents: readonly string[] }>
>;

export type LegalDocumentId =
  | 'guest-terms'
  | 'guest-how-it-works'
  | 'partner-terms'
  | 'conciergerie-contract'
  | 'bakery-contract'
  | 'legal-notice'
  | 'privacy'
  | 'cookies';

export function legalDocument(id: LegalDocumentId): LegalDocument {
  const found = LEGAL_DOCUMENTS.find((d) => d.id === id);
  if (found === undefined) throw new Error(`legal register: no document ${id}`);
  return found;
}

/**
 * Where a published document is read in a locale: its path on its host, and
 * the language that path is in. A document with no English version (the
 * partnership conditions) answers its French path with `locale: 'fr'`, so the
 * link can say so rather than pretend.
 */
export function legalPath(
  id: LegalDocumentId,
  locale: LegalLocale,
): { readonly host: LegalHost; readonly path: string; readonly locale: LegalLocale } {
  const doc = legalDocument(id);
  if (doc.host === null || doc.path === null)
    throw new Error(`legal register: ${id} is not published`);
  const own = locale === 'en' ? doc.path.en : doc.path.fr;
  return own === undefined
    ? { host: doc.host, path: doc.path.fr, locale: 'fr' }
    : { host: doc.host, path: own, locale };
}

/** The documents a surface links, in the order it lists them. */
export function surfaceDocuments(surface: LegalSurface): readonly LegalDocument[] {
  return LEGAL_SURFACES[surface].documents.map((id) => legalDocument(id as LegalDocumentId));
}
